import React from "react";
import html2canvas from "html2canvas";
import { Box, Grid, InputLabel } from "@mui/material";
import AvatarPanel from "./AvatarPanel.tsx";
import ExplainPrompt from "./ExplainPrompt.tsx";
import ThumbUpPrompt from "./ThumbUpPrompt.tsx";
import ThumbDownPrompt from "./ThumbDownPrompt.tsx";
import BarGraph, { validateBarGraph } from "../graphs/BarGraph";
import LineGraph, { validateLineGraph } from "../graphs/LineGraph";
import InvalidGraph from "../graphs/InvalidGraph";
import DownloadGraphPrompt from "../graphs/DownloadGraph.tsx";
import CustomReactMarkDown from "../../utils/markdown-wrapper.tsx";
import { useTypedSelector } from "../../hooks.tsx";
import { GraphErrorBoundary } from "./GraphErrorBoundary.tsx";

interface GraphPromptProps {
  children?: React.ReactNode;
  content: {
    data: Array<{ [key: string]: any }>;
    graph_type: string;
    x_axis?: string;
    y_axis?: string;
    clarification?: string;
  };
  feedback?: string | null;
  exchange_id?: string;
}

const GraphPrompt: React.FC<GraphPromptProps> = ({
  feedback,
  exchange_id,
  content: { data, graph_type, x_axis, y_axis, clarification },
}) => {
  const chartRef = React.useRef<null | HTMLDivElement>(null);
  const isLoggedIn = useTypedSelector((state) => state.authState.isLoggedIn);

  const handleDownloadGraph = async () => {
    if (chartRef.current) {
      const canvas = await html2canvas(chartRef.current);
      const graphImage = canvas.toDataURL("graph/export", 1.0);
      const fakeLink = window.document.createElement("a");
      fakeLink.download = "TallyChart";
      fakeLink.href = graphImage;

      document.body.appendChild(fakeLink);
      fakeLink.click();
      document.body.removeChild(fakeLink);

      fakeLink.remove();
    }
  };
  const getGraphTypeComponent = () => {
    if (graph_type === "line" && validateLineGraph(data, x_axis)) {
      return (
        <LineGraph
          content={data}
          xAxisKey={x_axis || ""}
          yAxisKey={y_axis || ""}
        />
      );
    } else if (graph_type === "bar" && validateBarGraph(data, x_axis)) {
      return (
        <BarGraph
          content={data}
          xAxisKey={x_axis || ""}
          yAxisKey={y_axis || ""}
        />
      );
    } else {
      return <InvalidGraph />;
    }
  };

  return (
    <Box marginBottom={"4%"}>
      <Grid
        container
        direction="row"
        spacing={2}
        alignContent="flex-start"
        alignItems="flex-start"
      >
        <Grid
          size={{ xs: 1, lg: 1 }}
          sx={{
            marginTop: "1%",
          }}
        >
          <AvatarPanel />
        </Grid>
        <Grid size={{ xs: 11, lg: 11 }}>
          <InputLabel
            sx={{
              color: "black",
              whiteSpace: "normal",
            }}
          >
            <div ref={chartRef}>
              <GraphErrorBoundary>{getGraphTypeComponent()}</GraphErrorBoundary>
            </div>
            {clarification && (
              <CustomReactMarkDown
                content={clarification}
              ></CustomReactMarkDown>
            )}
          </InputLabel>
        </Grid>
      </Grid>
      <Grid position={"relative"} sx={{ marginLeft: "8%", marginBottom: "5%" }}>
        <Grid container direction="row" position={"absolute"}>
          {feedback !== "bad" && (
            <Grid>
              <ThumbUpPrompt
                exchange_id={exchange_id}
                clicked={feedback === "good"}
              />
            </Grid>
          )}
          {feedback !== "good" && (
            <Grid>
              <ThumbDownPrompt
                exchange_id={exchange_id}
                clicked={feedback === "bad"}
              />
            </Grid>
          )}
          {isLoggedIn && (
            <Grid>
              <ExplainPrompt exchange_id={exchange_id} />
            </Grid>
          )}
          <Grid>
            <DownloadGraphPrompt handleDownloadGraph={handleDownloadGraph} />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GraphPrompt;
